import React, { useState, useRef } from 'react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { Link, useLocation } from 'wouter';
import { 
  ChevronLeft, ChevronRight, Laptop, Video, Globe, Users, ShieldCheck, 
  PanelRight, X, Share2, Clock, Lock, MonitorSmartphone, CheckCircle, 
  AlertTriangle, FileText, BookOpen, Smartphone, Lightbulb, Info, 
  Play, Maximize, Minimize, ArrowLeft
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };
  
  // Add event listeners for fullscreen change
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef}
      className={`relative ${isFullscreen ? 'fixed inset-0 z-50 flex items-center justify-center bg-white' : 'bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl overflow-hidden mb-10'}`}
    >
      <div className={`${isFullscreen ? 'px-10 py-20 w-full h-full flex items-center justify-center' : 'px-6 py-12 md:px-12 md:py-16'}`}>
        <div className={`${isFullscreen ? 'max-w-6xl w-full' : 'max-w-4xl mx-auto'}`}>
          <div className="space-y-6">
            <h2 className={`${isFullscreen ? 'text-3xl md:text-4xl text-blue-900' : 'text-2xl md:text-3xl text-blue-900'} font-serif text-center font-bold`}>
              {slides[currentSlide].title}
            </h2>
            <div className={`prose prose-lg max-w-none ${isFullscreen ? 'prose-strong:text-blue-700' : 'prose-strong:text-blue-700'} overflow-y-auto ${isFullscreen ? 'max-h-[calc(100vh-180px)]' : 'max-h-[60vh]'}`} style={{scrollbarWidth: 'thin'}}>
              {slides[currentSlide].content}
            </div>
            {slides[currentSlide].image && (
              <div className="mt-6 flex justify-center">
                <div className={`${isFullscreen ? 'bg-gray-50 p-3' : 'bg-white p-2'} rounded-lg shadow-lg`}>
                  <img 
                    src={slides[currentSlide].image} 
                    alt={slides[currentSlide].title}
                    className={`${isFullscreen ? 'max-h-[500px]' : 'max-h-[300px]'} object-contain rounded`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 text-gray-500 bg-gray-100 p-2 rounded-full hover:bg-gray-200"
        >
          <X className="h-6 w-6" />
        </button>
      )}

      <div className="absolute bottom-4 right-4 flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={prevSlide}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={nextSlide}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </Button>
      </div>

      <div className="absolute bottom-4 left-4">
        <span className={`text-sm font-medium ${isFullscreen ? 'bg-gray-100 text-gray-700' : 'bg-white/80'} px-2 py-1 rounded`}>
          {currentSlide + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

// Video component with embedded video from Descript
const VideoPlaceholder = () => {
  return (
    <div className="bg-gray-100 rounded-xl overflow-hidden my-10">
      <div className="aspect-video relative">
        <div className="relative w-full h-full">
          <iframe 
            src="https://share.descript.com/embed/fYOPyAhZWd8" 
            width="100%" 
            height="100%" 
            className="absolute inset-0"
            frameBorder="0" 
            allowFullScreen
            title="Telehealth for Longevity Practices"
          ></iframe>
        </div>
        <div className="mt-2 text-center">
          <p className="text-sm text-gray-500">Telehealth for Longevity Practices • Premium Content</p>
        </div>
      </div>
    </div>
  );
};

export default function TelehealthLecture() {
  const [activeTab, setActiveTab] = useState('overview');
  const [showToc, setShowToc] = useState(false);

  // Table of Contents
  const tocItems = [
    { id: 'intro', label: 'Introduction', offset: 0 },
    { id: 'regulations', label: 'Regulatory Landscape', offset: 200 },
    { id: 'platforms', label: 'Technology & Platforms', offset: 500 },
    { id: 'implementation', label: 'Implementation Guide', offset: 800 },
    { id: 'patient-experience', label: 'Optimizing Patient Experience', offset: 1100 },
    { id: 'case-studies', label: 'Case Studies', offset: 1400 },
  ];

  const scrollToSection = (offset: number) => {
    window.scrollTo({
      top: offset,
      behavior: 'smooth'
    });
    setShowToc(false);
  };

  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col relative bg-white">
      <Navbar />
      
      {/* Mobile TOC button */}
      <button
        onClick={() => setShowToc(!showToc)}
        className="md:hidden fixed bottom-6 right-6 z-40 bg-indigo-600 text-white p-3 rounded-full shadow-lg"
      >
        {showToc ? <X size={24} /> : <PanelRight size={24} />}
      </button>
      
      {/* Mobile TOC sidebar */}
      <div className={cn(
        "md:hidden fixed inset-y-0 right-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out pt-16",
        showToc ? "translate-x-0" : "translate-x-full"
      )}>
        <div className="p-4">
          <h3 className="font-bold text-lg mb-4 text-gray-900">Table of Contents</h3>
          <ul className="space-y-3">
            {tocItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => scrollToSection(item.offset)}
                  className="text-left text-indigo-600 hover:text-indigo-800 font-medium"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <main className="flex-1 container mx-auto px-4 py-8 md:flex md:gap-8">
        {/* Desktop TOC sidebar */}
        <aside className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
              <h3 className="font-bold text-lg mb-4 text-gray-900">Table of Contents</h3>
              <ul className="space-y-3">
                {tocItems.map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(item.offset)}
                      className="text-left text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </aside>
        
        {/* Main content */}
        <div className="flex-1 mt-6 md:mt-0">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-gray-600 mb-2">
              <Link href="/mentorship" className="hover:text-indigo-600">Mentorship</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/mentorship/business" className="hover:text-indigo-600">Business Lectures</Link>
              <ChevronRight className="h-4 w-4" />
              <span className="text-gray-900">Telehealth</span>
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 font-serif mb-4">Telehealth for Longevity Practices</h1>
            
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center text-gray-600">
                <Clock className="h-4 w-4 mr-1" />
                <span className="text-sm">50 min lecture</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Share2 className="h-4 w-4 mr-1" />
                <span className="text-sm">Mentorship Exclusive</span>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 mb-6">
              Master the implementation of effective telehealth services in your longevity practice. This comprehensive lecture
              covers everything from regulatory compliance to platform selection, with practical implementation strategies
              and case studies of successful longevity telehealth practices.
            </p>
          </div>
          
          <Tabs defaultValue="overview" className="mb-12" onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="slides">Slides</TabsTrigger>
              <TabsTrigger value="resources">Resources</TabsTrigger>
            </TabsList>
            
            <TabsContent value="overview" className="pt-6">
              {/* Slideshow presentation */}
              <Slideshow slides={[
                {
                  id: 1,
                  title: "Introduction to Telehealth in Longevity Medicine",
                  content: (
                    <>
                      <p>
                        Telehealth represents a transformative opportunity for longevity medicine practices, enabling expanded patient reach, enhanced monitoring capabilities, and more flexible care models. The COVID-19 pandemic accelerated telehealth adoption across healthcare, but longevity medicine—with its focus on proactive, data-driven care—is particularly well-positioned to leverage these technologies for optimal outcomes.
                      </p>
                      
                      <div className="bg-blue-50 p-6 rounded-lg my-6 border border-blue-100">
                        <h3 className="font-bold text-blue-800 mb-3">Key Advantages for Longevity Practices</h3>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                              <Globe className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                            <div>
                              <span className="font-medium">Geographic Reach</span>
                              <p className="text-sm text-blue-800 mt-1">Serve patients regardless of location, expanding your practice beyond local markets</p>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                              <MonitorSmartphone className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                            <div>
                              <span className="font-medium">Continuous Monitoring</span>
                              <p className="text-sm text-blue-800 mt-1">Integrate remote monitoring tools for real-time health data collection</p>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                              <CheckCircle className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                            <div>
                              <span className="font-medium">Efficiency</span>
                              <p className="text-sm text-blue-800 mt-1">Reduce overhead costs while maintaining high-quality care delivery</p>
                            </div>
                          </div>
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                              <Users className="h-3.5 w-3.5 text-blue-600" />
                            </div>
                            <div>
                              <span className="font-medium">Patient Convenience</span>
                              <p className="text-sm text-blue-800 mt-1">Reduce barriers to care with flexible scheduling and no travel requirements</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <p>
                        In this lecture, we'll explore how to effectively implement telehealth services in your longevity practice, navigating regulatory requirements, selecting appropriate technologies, and creating exceptional virtual care experiences that align with the high standards expected in premium longevity care.
                      </p>
                    </>
                  )
                },
                {
                  id: 2,
                  title: "Regulatory and Compliance Framework",
                  content: (
                    <>
                      <p>
                        Telehealth in the U.S. is governed by a complex patchwork of federal and state regulations. Clinicians must navigate licensing, privacy laws, and prescribing rules to ensure compliance.
                      </p>
                      
                      <h3 className="text-xl font-bold mt-6 mb-3">State Licensing & Interstate Practice:</h3>
                      
                      <div className="bg-blue-50 p-5 rounded-lg my-5 border border-blue-100">
                        <ul className="space-y-3">
                          <li className="flex items-start">
                            <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center mr-3 mt-0.5">
                              <span className="text-white text-xs font-bold">1</span>
                            </div>
                            <p className="text-blue-900">
                              <strong>Provider Location Rule:</strong> In the U.S., providers generally must be licensed in the state where the patient is located during a telehealth visit.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center mr-3 mt-0.5">
                              <span className="text-white text-xs font-bold">2</span>
                            </div>
                            <p className="text-blue-900">
                              <strong>Interstate Options:</strong> The Interstate Medical Licensure Compact (IMLC) streamlines multi-state licensing for physicians in participating states.
                            </p>
                          </li>
                          <li className="flex items-start">
                            <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-600 flex items-center justify-center mr-3 mt-0.5">
                              <span className="text-white text-xs font-bold">3</span>
                            </div>
                            <p className="text-blue-900">
                              <strong>Telehealth-Specific Registration:</strong> Some states (e.g., Florida) offer special telehealth provider registration that allows out-of-state practitioners to practice telehealth.
                            </p>
                          </li>
                        </ul>
                      </div>
                      
                      <div className="bg-amber-50 p-4 rounded-lg my-6 border border-amber-100">
                        <div className="flex items-start">
                          <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-amber-800 mb-1">Important Practice Note</h4>
                            <p className="text-amber-800 text-sm">
                              Always verify patient location at each visit and secure informed consent, since maintaining a valid provider-patient relationship via telehealth is legally required in most states.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 3,
                  title: "HIPAA and Data Privacy",
                  content: (
                    <>
                      <p className="mb-4">
                        All telehealth services must comply with HIPAA just as in-person care does. This is particularly important for longevity practices that handle sensitive patient data including genomics, biomarkers, and long-term health trends.
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-blue-50 p-5 rounded-lg border border-blue-100">
                          <h4 className="font-bold text-blue-800 mb-3 text-lg">Essential HIPAA Requirements</h4>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <ShieldCheck className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-900 text-sm">Use only HIPAA-compliant platforms that ensure encrypted, secure communication and data storage</p>
                            </li>
                            <li className="flex items-start">
                              <ShieldCheck className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-900 text-sm">Obtain Business Associate Agreements (BAAs) with all telehealth vendors who may access PHI</p>
                            </li>
                            <li className="flex items-start">
                              <ShieldCheck className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-900 text-sm">Train staff on telehealth-specific privacy protocols</p>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="bg-purple-50 p-5 rounded-lg border border-purple-100">
                          <h4 className="font-bold text-purple-800 mb-3 text-lg">Beyond HIPAA</h4>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <AlertTriangle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Be aware of emerging state-level privacy laws that may impose additional requirements on handling digital health data</p>
                            </li>
                            <li className="flex items-start">
                              <AlertTriangle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">For international patients, consider GDPR and other applicable regulations</p>
                            </li>
                            <li className="flex items-start">
                              <AlertTriangle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Provide patients with a specific "telehealth privacy notice" during onboarding</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <KeyTakeaway>
                        <p className="font-medium">
                          Longevity practices often deal with extra-sensitive data (genetic tests, cognitive assessments, etc.). Consider going beyond minimum compliance requirements to implement enhanced data protection measures—this protects both your practice and builds patient trust.
                        </p>
                      </KeyTakeaway>
                    </>
                  )
                },
                {
                  id: 4,
                  title: "Prescribing & Documentation Requirements",
                  content: (
                    <>
                      <h3 className="text-xl font-bold mb-4">Telehealth Prescribing Considerations</h3>
                      
                      <div className="bg-blue-50 p-5 rounded-lg mb-6 border border-blue-100">
                        <p className="text-blue-900 mb-4">
                          If your longevity practice involves prescribing medications (e.g., hormone therapies like testosterone or other controlled substances), pay special attention to DEA regulations.
                        </p>
                        
                        <div className="space-y-3">
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 border border-blue-200">
                              <span className="text-blue-700 text-sm font-medium">1</span>
                            </div>
                            <div>
                              <p className="text-blue-900"><strong>Ryan Haight Act:</strong> Traditionally required an in-person evaluation before prescribing controlled substances via telemedicine, though pandemic-era flexibilities have been extended through portions of 2025.</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 border border-blue-200">
                              <span className="text-blue-700 text-sm font-medium">2</span>
                            </div>
                            <div>
                              <p className="text-blue-900"><strong>State Variations:</strong> Some states impose additional restrictions on prescribing via telehealth, particularly for controlled substances. Verify state-specific rules for each patient location.</p>
                            </div>
                          </div>
                          
                          <div className="flex items-start">
                            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 border border-blue-200">
                              <span className="text-blue-700 text-sm font-medium">3</span>
                            </div>
                            <div>
                              <p className="text-blue-900"><strong>Documentation:</strong> Document telehealth encounters thoroughly, including the technology used, that informed consent for telehealth was obtained, and detailed assessment findings.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                        <div className="flex items-start">
                          <Info className="h-5 w-5 text-amber-600 mr-2 mt-0.5" />
                          <div>
                            <h4 className="font-bold text-amber-800 mb-1">Documentation Best Practice</h4>
                            <p className="text-amber-800 text-sm">
                              Many states require obtaining explicit patient consent for telehealth – typically a one-time form explaining how telemedicine works, its limitations (e.g., no physical exam), and privacy considerations. Keep telehealth consent forms separate from general medical consent.
                            </p>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 5,
                  title: "Telehealth Technology & Platforms",
                  content: (
                    <>
                      <p>
                        Choosing the right telehealth technology platform is crucial, especially for longevity medicine where care is data-intensive and longitudinal. You'll need more than just video calls – think integration of wearables, remote monitoring, and electronic health records (EHR).
                      </p>
                      
                      <h3 className="text-xl font-bold mt-6 mb-3">Comprehensive vs. Niche Platforms:</h3>
                      
                      <div className="grid md:grid-cols-2 gap-5 my-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                          <h4 className="font-bold text-gray-900 mb-3">Generic Telehealth Platforms</h4>
                          <ul className="text-sm text-gray-700 space-y-2">
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Doxy.me, Zoom for Healthcare, Amwell</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Secure video conferencing and basic EHR integration</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>Good for practices just starting with telehealth</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-gray-400 mr-2">•</span>
                              <span>May lack longevity-specific features</span>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-5 shadow-sm">
                          <h4 className="font-bold text-indigo-900 mb-3">Longevity-Focused Platforms</h4>
                          <ul className="text-sm text-indigo-900 space-y-2">
                            <li className="flex items-start">
                              <span className="text-indigo-400 mr-2">•</span>
                              <span>Platforms like Heads Up Health, Basis</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-indigo-400 mr-2">•</span>
                              <span>Aggregate wearable data, lab results, and lifestyle metrics</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-indigo-400 mr-2">•</span>
                              <span>Designed for personalized, long-term care</span>
                            </li>
                            <li className="flex items-start">
                              <span className="text-indigo-400 mr-2">•</span>
                              <span>May offer AI tools for data analysis</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold mt-6 mb-3">Integration Capabilities:</h3>
                      
                      <div className="grid md:grid-cols-3 gap-5 my-6">
                        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                          <div className="h-10 w-10 bg-indigo-100 rounded-lg flex items-center justify-center mb-3">
                            <ShieldCheck className="h-5 w-5 text-indigo-600" />
                          </div>
                          <h4 className="font-bold text-gray-900 mb-2">Security & Compliance</h4>
                          <ul className="text-sm text-gray-700 space-y-2">
                            <li>• HIPAA compliance certification</li>
                            <li>• End-to-end encryption</li>
                            <li>• BAA availability</li>
                            <li>• Data storage protocols</li>
                          </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                          <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                            <Video className="h-5 w-5 text-green-600" />
                          </div>
                          <h4 className="font-bold text-gray-900 mb-2">Technical Features</h4>
                          <ul className="text-sm text-gray-700 space-y-2">
                            <li>• Video/audio quality</li>
                            <li>• Screen sharing capabilities</li>
                            <li>• Mobile compatibility</li>
                            <li>• Bandwidth requirements</li>
                          </ul>
                        </div>
                        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                          <div className="h-10 w-10 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                            <FileText className="h-5 w-5 text-purple-600" />
                          </div>
                          <h4 className="font-bold text-gray-900 mb-2">Clinical Workflow</h4>
                          <ul className="text-sm text-gray-700 space-y-2">
                            <li>• EHR integration</li>
                            <li>• Scheduling functionality</li>
                            <li>• Documentation tools</li>
                            <li>• Patient portal features</li>
                          </ul>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 6,
                  title: "Wearables & Remote Monitoring Integration",
                  content: (
                    <>
                      <p className="mb-6">
                        Longevity medicine thrives on continuous data – daily steps, sleep quality, heart rate variability, glucose trends, blood pressure readings, and more. Your telehealth technology should be able to ingest and display wearable and home-monitoring data seamlessly.
                      </p>
                      
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg mb-6 border border-blue-100">
                        <h3 className="font-bold text-indigo-900 mb-4 text-xl">Key Considerations for Wearable Integration</h3>
                        
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-medium text-indigo-800 mb-3">Common Device Integrations:</h4>
                            <ul className="space-y-2">
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Apple Watch, Garmin, Oura Ring</span>
                              </li>
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Continuous Glucose Monitors (CGMs)</span>
                              </li>
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Blood pressure monitors</span>
                              </li>
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Sleep tracking devices</span>
                              </li>
                            </ul>
                          </div>
                          
                          <div>
                            <h4 className="font-medium text-indigo-800 mb-3">Data Visualization Needs:</h4>
                            <ul className="space-y-2">
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Trend analysis over time</span>
                              </li>
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Correlation between different metrics</span>
                              </li>
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Actionable insights, not just raw data</span>
                              </li>
                              <li className="flex items-start">
                                <div className="h-6 w-6 rounded-full bg-white border border-indigo-200 flex items-center justify-center mr-2 flex-shrink-0">
                                  <span className="text-indigo-600 text-sm">✓</span>
                                </div>
                                <span className="text-indigo-900">Patient and provider views</span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <KeyTakeaway>
                        <p className="font-medium mb-2">Remote Monitoring Best Practice</p>
                        <p>
                          In longevity practices, look for platforms that offer more than simple data collection. Consider solutions that apply AI or machine learning to highlight patterns in patient data, generate alerts for concerning readings, and provide predictive analytics. The ability to view longitudinal data during telehealth visits is particularly valuable.
                        </p>
                      </KeyTakeaway>
                    </>
                  )
                },
                {
                  id: 7,
                  title: "Patient Engagement Strategies",
                  content: (
                    <>
                      <p className="mb-5">
                        Building strong patient relationships is at the heart of longevity medicine. Telehealth, when used thoughtfully, can actually enhance engagement by providing more frequent touchpoints and convenient access.
                      </p>
                      
                      <h3 className="text-xl font-bold mb-4">Establishing "Webside" Manner and Trust</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-blue-50 p-5 rounded-lg border border-blue-100">
                          <h4 className="font-bold text-blue-800 mb-3">Digital Touchpoints Strategy</h4>
                          <ul className="space-y-3">
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-900 text-sm">Send regular secure check-ins between appointments: "How are your energy levels this week? Any issues with the new supplement?"</p>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-900 text-sm">Set up remote monitoring alerts: If a patient's wearable reports concerning data, trigger outreach</p>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-blue-900 text-sm">Schedule brief telehealth follow-ups more frequently than in-person visits</p>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="bg-purple-50 p-5 rounded-lg border border-purple-100">
                          <h4 className="font-bold text-purple-800 mb-3">Community and Education</h4>
                          <ul className="space-y-3">
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Host monthly "Longevity Q&A" group video sessions open to all patients</p>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Organize small group virtual workshops for patients with similar goals</p>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Deliver ongoing education in digestible bites (short videos, infographics) via secure messaging</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="bg-amber-50 p-5 rounded-lg border border-amber-100 mb-4">
                        <h4 className="font-bold text-amber-800 mb-3">Personalized Communication Framework</h4>
                        <p className="text-amber-900 mb-3">One size doesn't fit all in engagement. Develop a framework that segments patients by needs or risk, and tailor communication frequency and style.</p>
                        <ul className="space-y-2">
                          <li className="flex items-start">
                            <Lightbulb className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-amber-900 text-sm">Utilize multi-channel communication based on patient preference (text, email, portal messages)</p>
                          </li>
                          <li className="flex items-start">
                            <Lightbulb className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-amber-900 text-sm">Reference patient-generated data in communications to demonstrate attention to detail</p>
                          </li>
                          <li className="flex items-start">
                            <Lightbulb className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                            <p className="text-amber-900 text-sm">Create consistent rituals of connection in virtual visits to maintain relationship</p>
                          </li>
                        </ul>
                      </div>
                    </>
                  )
                },
                {
                  id: 8,
                  title: "Business Strategy & Monetization",
                  content: (
                    <>
                      <p className="mb-4">
                        A telehealth-enabled longevity clinic not only needs clinical savvy but also a sustainable business model. Here we examine pricing strategies, care delivery models, and operational workflows.
                      </p>
                      
                      <h3 className="text-xl font-bold mb-4">Pricing Models</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-5 rounded-lg border border-indigo-100">
                          <h4 className="font-bold text-indigo-900 mb-3">Membership/Subscription Model</h4>
                          <p className="text-indigo-800 mb-3 text-sm">Many longevity-focused practices opt for tiered membership levels:</p>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <div className="h-5 w-5 rounded-full bg-white border border-indigo-300 flex items-center justify-center mr-2 flex-shrink-0">
                                <span className="text-indigo-500 text-xs">$</span>
                              </div>
                              <p className="text-indigo-800 text-sm"><strong>Basic Wellness Plan:</strong> ~$175/month, includes basic health monitoring and coaching</p>
                            </li>
                            <li className="flex items-start">
                              <div className="h-5 w-5 rounded-full bg-white border border-indigo-300 flex items-center justify-center mr-2 flex-shrink-0">
                                <span className="text-indigo-500 text-xs">$$</span>
                              </div>
                              <p className="text-indigo-800 text-sm"><strong>Comprehensive Longevity Program:</strong> ~$499/month, includes advanced testing, more doctor time</p>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-lg border border-gray-200">
                          <h4 className="font-bold text-gray-900 mb-3">Fee-for-Service Model</h4>
                          <p className="text-gray-800 mb-3 text-sm">Some practices bill per telehealth visit:</p>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-gray-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-gray-800 text-sm">Medicare now reimburses many telehealth services at parity with in-person (at least through 2024)</p>
                            </li>
                            <li className="flex items-start">
                              <CheckCircle className="h-5 w-5 text-gray-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-gray-800 text-sm">Consider hybrid approach: base membership with fee-for-service for specialized consultations</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 p-5 rounded-lg border border-blue-100 mb-6">
                        <h4 className="font-bold text-blue-800 mb-3">Hybrid Care Delivery Model</h4>
                        <p className="text-blue-900 mb-3">
                          Design your model as hybrid: perhaps an initial in-person comprehensive evaluation, followed by predominantly virtual follow-ups.
                        </p>
                        <div className="flex items-start mb-2">
                          <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <span className="text-blue-700 font-medium text-sm">1</span>
                          </div>
                          <p className="text-blue-900">Annual/semi-annual in-person visit for full workup (physical exam, biomarker panels, fitness tests)</p>
                        </div>
                        <div className="flex items-start">
                          <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                            <span className="text-blue-700 font-medium text-sm">2</span>
                          </div>
                          <p className="text-blue-900">"Virtual clinic with local nodes": collaborate with labs nationwide for tests, use mobile phlebotomy for home blood draws, refer to local imaging centers</p>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 9,
                  title: "Operational Workflows & Scaling",
                  content: (
                    <>
                      <h3 className="text-xl font-bold mb-4">Scaling Your Practice with Care Teams</h3>
                      
                      <div className="bg-blue-50 p-5 rounded-lg border border-blue-100 mb-6">
                        <h4 className="font-bold text-blue-800 mb-3">Care Team Approach</h4>
                        <p className="text-blue-900 mb-3">
                          One clinician can only handle so many high-touch patients, even with telehealth efficiencies. Consider a care team approach to scale:
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <ul className="space-y-2">
                              <li className="flex items-start">
                                <Users className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <p className="text-blue-900 text-sm">Physicians focus on medical issues and treatment plans</p>
                              </li>
                              <li className="flex items-start">
                                <Users className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <p className="text-blue-900 text-sm">Nurse practitioners handle routine follow-ups</p>
                              </li>
                            </ul>
                          </div>
                          <div>
                            <ul className="space-y-2">
                              <li className="flex items-start">
                                <Users className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <p className="text-blue-900 text-sm">Health coaches manage lifestyle changes and accountability</p>
                              </li>
                              <li className="flex items-start">
                                <Users className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                                <p className="text-blue-900 text-sm">Nutritionists/dietitians provide specialized support</p>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-bold mb-4">Patient Journey Workflow</h3>
                      
                      <div className="space-y-4">
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="font-bold text-blue-700">1</span>
                          </div>
                          <div>
                            <h4 className="font-bold">Structured Onboarding</h4>
                            <p className="text-gray-700 text-sm">Create a comprehensive virtual onboarding process including online intake questionnaires, welcome calls, and digital welcome kits that explain how your practice works</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="font-bold text-blue-700">2</span>
                          </div>
                          <div>
                            <h4 className="font-bold">Follow-up Cadence</h4>
                            <p className="text-gray-700 text-sm">Establish clear follow-up schedules with automated reminders: quick check-ins (15 min) for monitoring progress, longer follow-ups with labs at appropriate intervals</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="font-bold text-blue-700">3</span>
                          </div>
                          <div>
                            <h4 className="font-bold">Team Collaboration</h4>
                            <p className="text-gray-700 text-sm">Use telehealth platforms with team communication features for seamless handoffs between providers, creating a unified experience for patients</p>
                          </div>
                        </div>
                        
                        <div className="flex items-start">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <span className="font-bold text-blue-700">4</span>
                          </div>
                          <div>
                            <h4 className="font-bold">Quality Monitoring</h4>
                            <p className="text-gray-700 text-sm">Track metrics such as no-show rates, patient satisfaction, and clinical outcomes to continuously refine your telehealth operations</p>
                          </div>
                        </div>
                      </div>
                    </>
                  )
                },
                {
                  id: 10,
                  title: "Global Telehealth Insights",
                  content: (
                    <>
                      <p className="mb-5">
                        To broaden your perspective, it's useful to learn from telehealth models around the world. Longevity and preventive care are global pursuits, and different health systems have pioneered unique approaches.
                      </p>
                      
                      <div className="grid md:grid-cols-2 gap-6 mb-6">
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-100">
                          <h4 className="font-bold text-indigo-900 mb-3">Europe's Digital Health Integration</h4>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <Globe className="h-5 w-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-indigo-900 text-sm"><strong>France:</strong> Went from near-zero teleconsultations pre-2020 to millions of reimbursed telehealth visits post-pandemic</p>
                            </li>
                            <li className="flex items-start">
                              <Globe className="h-5 w-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-indigo-900 text-sm"><strong>Germany:</strong> Expanded coverage to include remote patient monitoring for chronic conditions through public insurance</p>
                            </li>
                            <li className="flex items-start">
                              <Globe className="h-5 w-5 text-indigo-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-indigo-900 text-sm"><strong>UK:</strong> The NHS partnered with digital-first primary care providers like GP at Hand (Babylon Health)</p>
                            </li>
                          </ul>
                        </div>
                        
                        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-5 rounded-lg border border-purple-100">
                          <h4 className="font-bold text-purple-900 mb-3">Japan's Aging-Focused Solutions</h4>
                          <ul className="space-y-2">
                            <li className="flex items-start">
                              <Globe className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">With ~29% of population over 65, Japan turned to telehealth and robotics out of necessity</p>
                            </li>
                            <li className="flex items-start">
                              <Globe className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Remote monitoring devices track vitals of seniors 24/7, with data transmitted to healthcare providers</p>
                            </li>
                            <li className="flex items-start">
                              <Globe className="h-5 w-5 text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
                              <p className="text-purple-900 text-sm">Integration of telehealth with smart home technology and AI diagnostic assist tools</p>
                            </li>
                          </ul>
                        </div>
                      </div>
                      
                      <KeyTakeaway>
                        <p className="font-medium mb-2">Global Lessons for Longevity Practices</p>
                        <p>
                          Successful telehealth models worldwide are characterized by accessibility, patient-centric design, and integration into broader healthcare systems. As you build your longevity practice, keep an eye on international telehealth trends – a solution pioneered abroad (like AI health coaches or group medical visits online) could be your next competitive advantage.
                        </p>
                      </KeyTakeaway>
                    </>
                  )
                },
                {
                  id: 11,
                  title: "Future Trends & Conclusion",
                  content: (
                    <>
                      <div className="border-gray-200 pt-4">
                        <h3 className="text-xl font-bold mb-4">Emerging Telehealth Trends in Longevity Medicine</h3>
                        
                        <div className="space-y-5 mb-6">
                          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-100">
                            <h4 className="font-bold text-indigo-900 mb-2">AI-Assisted Care Coordination</h4>
                            <p className="text-indigo-800 text-sm">
                              AI tools are increasingly being integrated into telehealth platforms to analyze patient data, draft visit summaries, and highlight important trends from wearable data. This reduces administrative burden while improving care quality.
                            </p>
                          </div>
                          
                          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-5 rounded-lg border border-purple-100">
                            <h4 className="font-bold text-indigo-900 mb-2">Immersive Health Experiences</h4>
                            <p className="text-indigo-800 text-sm">
                              Beyond simple video calls, the future of telehealth includes more immersive experiences using AR/VR for education, visualization of health data, and even virtual group wellness sessions.
                            </p>
                          </div>
                          
                          <div className="bg-gradient-to-r from-green-50 to-teal-50 p-5 rounded-lg border border-green-100">
                            <h4 className="font-bold text-green-900 mb-2">Biomarker Integration</h4>
                            <p className="text-green-800 text-sm">
                              Home-based and mail-in testing for advanced biomarkers (including epigenetic testing, microbiome analysis, and metabolomics) will increasingly be incorporated into telehealth longevity platforms.
                            </p>
                          </div>
                        </div>
                        
                        <p>
                          Telehealth represents a significant opportunity for longevity medicine practices to expand their reach, enhance patient monitoring, and create more flexible care models. By addressing the regulatory, technical, and clinical aspects of implementation, practices can successfully integrate virtual care in ways that maintain or even enhance the premium experience patients expect.
                        </p>
                        <p className="mt-4">
                          The future of longevity medicine will increasingly blend in-person and virtual care, with the distinction between the two becoming less relevant than the overall quality and personalization of the experience. Practices that master telehealth implementation now will be well-positioned to lead in this evolving landscape.
                        </p>
                      </div>
                      
                      <div className="mt-8">
                        <KeyTakeaway>
                          <p className="font-medium mb-2">Final Thought: The Human Touch in Virtual Care</p>
                          <p>Make virtual care "high-touch" even though it's high-tech. Longevity medicine often requires behavior change, and behavior change thrives on consistent encouragement, accountability, and a caring relationship. Telehealth gives you the tools to be in your patients' lives more regularly – use those tools creatively to maintain the human connection that is central to effective healthcare.</p>
                        </KeyTakeaway>
                      </div>
                    </>
                  )
                }
              ]} />
              
              {/* Video Placeholder at the bottom */}
              <div className="mt-12">
                <VideoPlaceholder />
              </div>
            </TabsContent>
            
            <TabsContent value="slides" className="pt-6">
              <div className="bg-gray-100 p-8 rounded-lg text-center flex items-center justify-center min-h-[400px]">
                <div>
                  <div className="mb-6">
                    <Laptop className="h-16 w-16 text-indigo-600 mx-auto" />
                  </div>
                  <h3 className="text-xl font-medium mb-4">Presentation Slides</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Downloadable slides from this lecture are available in PDF format.
                  </p>
                  <Button className="bg-indigo-600 hover:bg-indigo-700">
                    Download Slides
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="resources" className="pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 rounded-lg p-6 border border-blue-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Telehealth Implementation Toolkit</h3>
                  <p className="text-gray-700 mb-4">
                    Comprehensive toolkit including checklists, templates, policies, 
                    and procedures for implementing telehealth in your longevity practice.
                  </p>
                  <Button className="bg-blue-600 hover:bg-blue-700">Download Toolkit</Button>
                </div>
                
                <div className="bg-green-50 rounded-lg p-6 border border-green-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Patient Education Materials</h3>
                  <p className="text-gray-700 mb-4">
                    Ready-to-use patient education materials including telehealth 
                    guides, consent forms, and technical troubleshooting resources.
                  </p>
                  <Button className="bg-green-600 hover:bg-green-700">Download Materials</Button>
                </div>
                
                <div className="bg-amber-50 rounded-lg p-6 border border-amber-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">State Telehealth Regulations Guide</h3>
                  <p className="text-gray-700 mb-4">
                    Updated guide (2025) to telehealth regulations by state, including
                    licensing requirements, prescribing rules, and reimbursement policies.
                  </p>
                  <Button className="bg-amber-600 hover:bg-amber-700">Download Guide</Button>
                </div>
                
                <div className="bg-purple-50 rounded-lg p-6 border border-purple-100">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Remote Monitoring Integration Guide</h3>
                  <p className="text-gray-700 mb-4">
                    Detailed guide on integrating remote monitoring devices into your
                    telehealth practice, including device selection and data interpretation.
                  </p>
                  <Button className="bg-purple-600 hover:bg-purple-700">Download Guide</Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="mt-12 border-t border-gray-200 pt-8 flex justify-between">
            <Link href="/mentorship/business">
              <Button variant="outline" className="flex items-center">
                <ChevronLeft className="mr-2 h-4 w-4" />
                Back to Business Lectures
              </Button>
            </Link>
            <Link href="/mentorship/business/pricing-strategies">
              <Button className="bg-indigo-600 hover:bg-indigo-700 flex items-center">
                Next Lecture: Pricing Strategies
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}